import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
  ArrowIcon,
  ButtonLink,
  DrawerMyCompanies,
  DrawerUserProfile,
  Header,
  PersonIcon,
  Presence,
  Sidebar,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import RoleTag from "../../components/RoleTag";
import { useMenuCompany } from "./MenuCompany";
import { useMenuEmployee } from "./MenuEmployee";
import { CompanyDataContext } from "./CompanyDataContext";
import * as S from "./styles";
import { PublicLoading } from "../Pages/Common/PublicPage";
import {
  PageHeaderContext,
  PoweredBy,
  TenantLogo,
  ViewSwitch,
  applyTenantTheme,
  OW_ASSETS,
  escapeHtml,
} from "@components/ow";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import {
  clearActiveCompany,
  getActiveCompanyId,
  setActiveCompanyId,
  watchActiveCompanyChanges,
} from "../../helpers/activeCompany";
import { hasCompanyView } from "../../helpers/companyScreens";
import { apiFileUrl } from "../../helpers/apiFile";

// Seleção de empresa que volta para a tela que estava sendo aberta (link salvo,
// aba nova, reload), em vez de sempre cair no início
function selectCompanyPath(returnTo) {
  return `/auth/select-company?returnTo=${encodeURIComponent(returnTo)}`;
}

// Visão que a rota impõe: /c/ é da Empresa, /e/ é do Colaborador; as demais
// (/, /profile, /config, /dev/...) ficam com a visão salva
function viewModeFromPath(pathname) {
  if (pathname.startsWith("/c/")) return "company";
  if (pathname.startsWith("/e/")) return "employee";
  return null;
}

// Intervalo mínimo entre duas releituras das permissões (troca de rota e foco da aba)
const PERMISSIONS_REFRESH_MS = 5000;

// (11) 98765-4321, como na página de perfil
function formatPhone(value) {
  return String(value || "")
    .replace(/\D/g, "")
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(-\d{4})\d+?$/, "$1");
}

// Papel na empresa, na etiqueta de cada empresa do drawer "Minhas Empresas"
// Permissões e escopos num texto comparável, sem depender da ordem vinda da API
function permissionsSignature(permissions, scopes) {
  const scopeEntries = Object.keys(scopes || {})
    .sort()
    .map((key) => {
      const scope = scopes[key] || {};
      return [key, scope.all ? "all" : [...(scope.ids || [])].sort((a, b) => a - b)];
    });
  return JSON.stringify([[...(permissions || [])].sort(), scopeEntries]);
}

export default function Layout() {
  const authService = useService("auth");
  const userPermissionsService = useService("userPermissions");
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  const userService = useService("users");
  const [dataUser, setDataUser] = useState({});
  const [ready, setReady] = useState(false);
  // Logo reduzido no Sidebar; sem logo, o TenantLogo mostra o OneWork
  const [logo, setLogo] = useState({});
  // Cores da empresa: alimentam o applyTenantTheme(), que troca as vars --ow-* lidas pelo LIB_THEME
  const [tenantColors, setTenantColors] = useState({});
  const [showCompanies, setShowCompanies] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  // Telefone do drawer de perfil: o /me não traz, vem do usuário ao abrir o drawer
  const [phone, setPhone] = useState();
  // Título e breadcrumb que a tela informa (usePageHeader); sem eles, "Olá, Nome"
  const [pageHeader, setPageHeader] = useState(null);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("viewMode_ow") || "employee"
  );
  // Muda quando as permissões mudam: remonta a tela atual (guard e botões recalculados)
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const readyRef = useRef(false);
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);
  const firstPathRef = useRef(true);

  const canViewCompany = hasCompanyView(dataUser);
  const showViewSwitch = dataUser.role === "EMP" && canViewCompany;
  // Para quem vê as duas visões, a rota aberta manda; nas rotas compartilhadas, a salva
  const routeViewMode = showViewSwitch ? viewModeFromPath(location.pathname) : null;
  const currentViewMode = routeViewMode || viewMode;
  const showCompanyMenu = dataUser?.role === "ADM" || (canViewCompany && currentViewMode === "company");
  const companyMenu = useMenuCompany();
  const employeeMenu = useMenuEmployee(ready && !showCompanyMenu);
  const menu = showCompanyMenu ? companyMenu : employeeMenu;

  function switchViewMode(mode) {
    setViewMode(mode);
    localStorage.setItem("viewMode_ow", mode);
    navigate("/");
  }

  useEffect(() => {
    checkUserLogged();
  }, []);

  // Uma empresa ativa por navegador: se outra aba trocar de empresa (ou sair),
  // esta recarrega em vez de seguir mostrando dados de uma empresa e gravando em outra.
  useEffect(() => {
    return watchActiveCompanyChanges(() => {
      toast.info("A empresa ativa foi alterada em outra aba. Atualizando…");
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    });
  }, []);

  // Abrir uma tela /c/ ou /e/ (link, URL, voltar do navegador) leva a visão junto,
  // para as rotas compartilhadas abertas depois. Sem viewMode nas dependências: o
  // clique no seletor grava a outra visão e navega para "/", e isso não é desfeito.
  useEffect(() => {
    if (!routeViewMode) return;
    setViewMode(routeViewMode);
    localStorage.setItem("viewMode_ow", routeViewMode);
  }, [routeViewMode]);

  // Permissões mudadas pelo ADM valem sem novo login: relê o /me ao trocar de rota
  // (a primeira já vem do checkUserLogged) e quando a aba volta ao foco.
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === "visible") refreshPermissions();
    }
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tema da empresa no <html>: vale para a aplicação inteira, inclusive modais em portal
  useEffect(() => {
    applyTenantTheme(document.documentElement, tenantColors);
  }, [tenantColors]);

  // Telefone a cada abertura do drawer de perfil: ele muda em /profile
  useEffect(() => {
    if (!showProfile || !dataUser.id) return;
    userService
      .get(dataUser.id)
      .then((res) => setPhone(formatPhone(res?.data?.phone)))
      .catch(() => setPhone(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showProfile]);

  async function refreshPermissions() {
    if (!readyRef.current || refreshingRef.current) return;
    if (Date.now() - lastRefreshRef.current < PERMISSIONS_REFRESH_MS) return;
    refreshingRef.current = true;
    lastRefreshRef.current = Date.now();
    try {
      const resp = await userPermissionsService.me();
      if (!resp?.data) return;
      const permissions = resp.data.permissions || [];
      const scopes = resp.data.scopes || {};
      const stored = JSON.parse(localStorage.getItem("user_ow") || "{}");
      if (permissionsSignature(permissions, scopes) === permissionsSignature(stored.permissions, stored.scopes)) {
        return;
      }

      localStorage.setItem("user_ow", JSON.stringify({ ...stored, permissions, scopes }));
      setDataUser((prev) => ({ ...prev, permissions, scopes }));
      setPermissionsVersion((v) => v + 1);
      toast.info("Suas permissões foram atualizadas");

      // Sem nenhuma permissão da Visão Empresa, as telas /c/ deixam de existir para o EMP
      if (
        stored.role === "EMP" &&
        window.location.pathname.startsWith("/c/") &&
        !hasCompanyView({ permissions })
      ) {
        navigate("/");
      }
    } catch (err) {
      // Fica o que já estava; o back continua barrando o que não for permitido
      console.error("Erro ao atualizar permissões:", err);
    } finally {
      refreshingRef.current = false;
    }
  }

  // Logo do Sidebar, tema da empresa e as vars antigas das telas não migradas
  function applyCompanyBrand(companyData) {
    const logos = companyData.logos || {};
    const full = logos.normal_logo || logos.small_logo;
    const symbol = logos.small_logo || logos.normal_logo;
    setLogo({
      full: full ? apiFileUrl(full) : undefined,
      symbol: symbol ? apiFileUrl(symbol) : undefined,
      name: companyData.name,
    });

    const companyColors = companyData.colors || {};
    setTenantColors({
      primary: companyColors.primary,
      secondary: companyColors.secondary,
      textColor: companyColors.textColor,
    });
  }

  // Dados da empresa salvos em /config: a casca e o user_ow (lido por outras telas,
  // como o canSendAnonymous das declarações) mudam sem esperar um novo login
  function updateCompanyData(changes) {
    const stored = JSON.parse(localStorage.getItem("user_ow") || "{}");
    const companyData = { ...(stored.companyData || {}), ...changes };
    localStorage.setItem("user_ow", JSON.stringify({ ...stored, companyData }));
    setDataUser((prev) => ({ ...prev, companyData: { ...(prev.companyData || {}), ...changes } }));
    applyCompanyBrand(companyData);
  }

  async function checkUserLogged() {
    // Antes de qualquer await: depois dele a URL já pode ser outra
    const requestedPath = window.location.pathname + window.location.search;
    try {
      const me = await authService.me("complete=1");

      // Verifica se houve erro de autenticação
      if (me?.response?.status === 403 || me?.response?.status === 401) {
        throw new Error('Token expirado ou inválido');
      }

      // Verifica se a resposta é válida
      if (!me || !me.data) {
        throw new Error('Resposta inválida do servidor');
      }

      const userData = me.data;

      // Multi-empresa: verificar seleção de empresa
      if (userData.companies && userData.companies.length > 1) {
        const savedCompanyId = getActiveCompanyId();
        if (!savedCompanyId) {
          // Não tem empresa selecionada — redirecionar para seleção
          navigate(selectCompanyPath(requestedPath));
          return;
        }
        // Verificar se a empresa salva está nos vínculos
        const validCompany = userData.companies.find(
          (c) => String(c.companyId) === String(savedCompanyId)
        );
        if (!validCompany) {
          clearActiveCompany();
          navigate(selectCompanyPath(requestedPath));
          return;
        }
      } else if (userData.companies && userData.companies.length === 1) {
        // Só uma empresa — selecionar automaticamente
        setActiveCompanyId(userData.companies[0].companyId);
      } else if (userData.companies && userData.companies.length === 0) {
        // Sem empresas vinculadas
        navigate('/auth/select-company');
        return;
      }

      // Carregar permissões granulares do usuário na empresa ativa
      try {
        const permsResp = await userPermissionsService.me();
        userData.permissions = permsResp?.data?.permissions || [];
        userData.scopes = permsResp?.data?.scopes || {};
      } catch (permErr) {
        console.error('Erro ao carregar permissões do usuário:', permErr);
        userData.permissions = [];
        userData.scopes = {};
      }

      setDataUser(userData);

      // Limpa contador de tentativas em caso de sucesso
      sessionStorage.removeItem('retry_count');

      localStorage.setItem("user_ow", JSON.stringify(userData));
      if (userData.companyData) applyCompanyBrand(userData.companyData);

      setReady(true);
      readyRef.current = true;
      lastRefreshRef.current = Date.now();
    } catch (err) {
      console.error('Erro ao verificar usuário:', err);

      const token = localStorage.getItem('idToken_ow');

      // Se token expirado ou inválido, limpa e redireciona
      if (err.message === 'Token expirado ou inválido') {
        console.log('Token expirado, limpando e redirecionando para login');
        localStorage.removeItem('idToken_ow');
        localStorage.removeItem('uid_ow');
        localStorage.removeItem('user_ow');
        clearActiveCompany();
        window.location.href = "/auth/entrar";
        return;
      }

      // Se não houver token, redireciona para login
      if (!token) {
        console.log('Token não encontrado, redirecionando para login');
        window.location.href = "/auth/entrar";
      } else {
        // Se erro de rede ou temporário, tenta novamente apenas 3 vezes
        const retryCount = sessionStorage.getItem('retry_count') || '0';
        const retries = parseInt(retryCount);

        if (retries < 3) {
          console.error(`Erro ao buscar dados do usuário. Tentativa ${retries + 1}/3...`);
          sessionStorage.setItem('retry_count', (retries + 1).toString());
          setTimeout(() => {
            checkUserLogged();
          }, 2000);
        } else {
          console.error('Máximo de tentativas atingido. Limpando sessão...');
          sessionStorage.removeItem('retry_count');
          localStorage.removeItem('idToken_ow');
          localStorage.removeItem('uid_ow');
          localStorage.removeItem('user_ow');
          clearActiveCompany();
          window.location.href = "/auth/entrar";
        }
      }
    }
  }

  async function switchCompany(companyId) {
    try {
      const response = await authService.switchCompany(companyId);
      if (response && !response.error) {
        // Também reseta a visão: cada empresa pode ter um papel diferente
        setActiveCompanyId(companyId);
        localStorage.setItem('user_ow', JSON.stringify(response));
        window.location.href = "/";
      }
    } catch (err) {
      console.error('Erro ao trocar empresa:', err);
    }
  }

  async function logoutApp() {
    try {
      localStorage.clear();
      sessionStorage.clear();
      logout({ logoutParams: { returnTo: window.location.origin } })
    } catch (err) {
      console.log(err);
    }
  }

  const isAdmin = dataUser.role === "ADM";
  const companyData = dataUser.companyData || {};
  const companies = dataUser.companies || [];
  const otherCompanies = companies
    .filter((company) => String(company.companyId) !== String(dataUser.companyId))
    .map((company) => ({
      id: company.companyId,
      tag: <RoleTag role={company.role} />,
      surname: company.companyName || "",
      identification: company.cnpj || "",
    }));

  // Para ADM abre mesmo com uma empresa só: é por aqui que ele cadastra outra
  const canOpenCompanies = companies.length > 1 || isAdmin;

  const firstName = (dataUser.name || "").trim().split(/\s+/)[0];

  const viewSwitch = showViewSwitch ? <ViewSwitch value={currentViewMode} onChange={switchViewMode} /> : null;

  return (
    <>
      {!ready && <PublicLoading text="Carregando" />}

      {ready && (
        <>
          <PageHeaderContext.Provider value={setPageHeader}>
          <CompanyDataContext.Provider value={updateCompanyData}>
            <S.Root>
              <Sidebar
                $mainMenu={menu.mainMenu}
                $subMenu={menu.subMenu}
                $logo={<TenantLogo kind="symbol" src={logo.symbol} companyName={logo.name} />}
                $logoSecondary={
                  <TenantLogo
                    kind="symbol"
                    src={logo.symbol}
                    companyName={logo.name}
                    fallbackSymbol={OW_ASSETS.symbolLight}
                  />
                }
                $homeRoute="/"
                $returnLabel="Voltar"
                $returnIcon={<ArrowIcon />}
                $followRoute
              />

              <S.Column>
                {/* O Header monta o título e o "Olá" como HTML: nome digitado vai escapado */}
                <Header
                  $userName={escapeHtml(firstName)}
                  $title={pageHeader ? escapeHtml(pageHeader.title) : undefined}
                  $breadcrumb={pageHeader ? pageHeader.breadcrumb : undefined}
                  $menuStart={viewSwitch}
                  $actions={[{ icon: <PersonIcon $color="N400" />, title: "Perfil", onClick: () => setShowProfile(true) }]}
                  $company={{
                    name: companyData.name,
                    document: companyData.cnpj,
                    onClick: () => {
                      if (canOpenCompanies) setShowCompanies(true);
                    },
                  }}
                />

                <S.Content $isLibPage={!!pageHeader}>
                  <Outlet key={permissionsVersion} />
                </S.Content>

                <PoweredBy />
              </S.Column>
            </S.Root>

            <Presence show={showCompanies}>
              <DrawerMyCompanies
                $showDrawer={showCompanies}
                $setShowDrawer={() => setShowCompanies(false)}
                $current={{
                  tag: <RoleTag role={dataUser.role} />,
                  surname: companyData.name || "",
                  identification: companyData.cnpj || "",
                }}
                $companies={otherCompanies}
                $onSelect={(id) => {
                  setShowCompanies(false);
                  switchCompany(id);
                }}
                $onViewProfile={isAdmin ? () => navigate("/config") : undefined}
                $textSeeProfile={isAdmin ? "Ver dados" : ""}
                $onCreate={isAdmin ? () => navigate("/c/companies/new") : undefined}
                $textNoResults="Nenhuma outra empresa encontrada"
              />
            </Presence>

            <Presence show={showProfile}>
              <DrawerUserProfile
                $showDrawer={showProfile}
                $setShowDrawer={() => setShowProfile(false)}
                $user={{ fullName: dataUser.name, login: dataUser.email, email: dataUser.email, phone }}
                $onLogout={logoutApp}>
                <ButtonLink
                  $text="Editar perfil"
                  $action={() => {
                    setShowProfile(false);
                    navigate("/profile");
                  }}
                  $hasIcon
                />
              </DrawerUserProfile>
            </Presence>
          </CompanyDataContext.Provider>
          </PageHeaderContext.Provider>
        </>
      )}
    </>
  );
}
